import { useEffect, useState, useRef } from 'react'
import ConnectWallet from 'src/ui/molecules/ConnectWallet'
import Container from 'src/ui/molecules/Container'
import StatusChip from 'src/ui/atoms/StatusChip'
import Chip from 'src/ui/atoms/Chip'
import DeleteProject from './actions/common/DeleteProject'
import SubscribeProject from './actions/common/SubscribeProject'
import CheckValidateProject from './actions/common/CheckValidateProject'
import ValidateProject from './actions/common/ValidateProject'
import NFTProject from './NFTProject'
import TokenProject from './TokenProject'
import useProjectApi, { IProject } from './useProjectApi'
import ScProject from './ScProject'

interface IProjectTypes {
  token: string
  nft: string
  vesting: string
  staking: string
}

const projectTypes: IProjectTypes = {
  token: 'Token',
  nft: 'NFT Collection',
  vesting: 'Vesting Contract',
  staking: 'Staking Contract',
}

const Project = () => {
  const hasEffectRun = useRef(false)
  const { getProject } = useProjectApi()
  const [hasData, setHasData] = useState<boolean>(false)
  const [project, setProject] = useState<IProject>({
    id: 0,
    txHash: '',
    status: '',
    projectType: '',
    abi: [],
    scAddress: '',
    hasSubscription: false,
    data: {
      tokenName: '',
      tokenTicker: '',
      tokenSymbol: '',
      detailedTokenTicker: '',
      nftSymbol: '',
      nftCollectionName: '',
      contractName: '',
      chain: '',
      network: '',
      networkProvider: '',
      initialSupply: 0,
      numberOfDecimals: 0,
    },
    properties: {
      canMint: true,
      canBurn: true,
      canAddSpecialRoles: true,
      canChangeOwner: true,
      canFreeze: true,
      canPause: true,
      canUpgrade: true,
      canWipe: true,
    },
  })

  const fetchData = async () => {
    const resp = await getProject()

    setHasData(true)
    setProject(resp)
  }

  useEffect(() => {
    if (!hasEffectRun.current) {
      hasEffectRun.current = true

      fetchData()
    }
  }, [])

  if (!hasData) {
    return
  }

  let title = project.data.tokenName || project.data.nftCollectionName || project.data.contractName
  let chain = project.data.chain
  let projectType = project.projectType
  let hasSubscription = !!project.hasSubscription
  let network = project.data.network
  let status = project.status

  let statusType = (value: any) => {
    if (value === 'pending') {
      return 'pending'
    } else if (value === 'failed') {
      return 'error'
    } else if (value === 'successful' || value === 'verified') {
      return 'processed'
    }

    return 'new'
  }

  const data =
    project.projectType === 'token' || project.projectType === 'nft'
      ? [
          {
            type: 'Type',
            value: projectTypes[project.projectType as keyof IProjectTypes],
          },
          {
            type: 'Symbol',
            value:
              project.data.detailedTokenTicker ||
              project.data.tokenTicker ||
              project.data.tokenSymbol ||
              project.data.nftSymbol,
          },
          {
            type: 'Status',
            value: project.status,
          },
          {
            type: 'Initial supply',
            value: project.data.initialSupply,
          },
          {
            type: 'Decimals',
            value: project.data.numberOfDecimals,
          },
        ]
      : [
          {
            type: 'Type',
            value: projectTypes[project.projectType as keyof IProjectTypes],
          },
          {
            type: 'Status',
            value: project.status,
          },
        ]

  return (
    <Container
      title={title}
      action={
        <>
          {status !== 'verified' && (
            <>
              {chain !== 'multiversx' && (
                <CheckValidateProject project={project} reFetch={fetchData} />
              )}
              <ValidateProject project={project} reFetch={fetchData} />
            </>
          )}
          {chain === 'ethereum' && network === 'mainnet' && (
            <SubscribeProject hasSubscription={hasSubscription} />
          )}

          <DeleteProject />
        </>
      }>
      <div className="grid gap-12">
        <div className="grid gap-4">
          <h2 className="text-white">Chain & Network</h2>
          <div className="flex flex-row gap-4">
            <span className="flex items-center rounded-lg border px-4 py-1 text-sm text-main-blue">
              <span className={`icon-${chain.toLowerCase()} mr-2 text-xl`} />
              <span className="capitalize">{chain}</span>
            </span>
            <span className="flex items-center rounded-lg border px-4 py-1 text-sm text-main-blue">
              <span className="capitalize">{network}</span>
            </span>
          </div>
        </div>

        <ConnectWallet network={network} chain={chain} />

        <div className="grid gap-4">
          <h2>Details</h2>
          <div className="flex flex-col gap-2">
            {data.map(
              ({ type, value }, key) =>
                value && (
                  <p className="flex items-center gap-3" key={key}>
                    <span className="w-2/12 font-light">{type}</span>
                    {type === 'Status' ? (
                      <StatusChip name={value} type={statusType(value)} />
                    ) : (
                      <Chip name={value} mouseEffect={false} />
                    )}
                  </p>
                ),
            )}
          </div>
        </div>

        {projectType === 'token' && <TokenProject project={project} />}
        {projectType === 'nft' && <NFTProject project={project} />}
        {(projectType === 'vesting' || projectType === 'staking') && project.abi && (
          <ScProject project={project} />
        )}
      </div>
    </Container>
  )
}

export default Project
